import React, { useMemo, type PropsWithChildren } from "react";
import { SimpleGrid } from "@chakra-ui/react";
import { Section, sectionSpacingY } from "components/section";
import { useWarehouseCdp, type WarehouseConfig } from "contexts/warehouse-cdp-context";
import { TextColumn, TextTitle } from "ui/text";
import {
  ClipboardIcon,
  CodeIcon,
  EyeSlashIcon,
  GlobeIcon,
  LeversIcon,
  MailIcon,
  MapIcon,
  MegaphoneIcon,
  MessageIcon,
  MoneyIcon,
  PersonWithCheckIcon,
  SendIcon,
  SettingsIcon,
  SparklesIcon,
  TargetIcon,
  TrendIcon,
} from "ui/icons";
import { FilterTabs } from "ui/tabs";
import { type IconChipProps } from "ui/chips";
import { integrationsCount } from "../../../constants/integrations";

type FilterTabSection = {
  label: string;
  items: Array<{ iconChip: IconChipProps; title: string; description: string }>;
};

function getTabSections(warehouse: WarehouseConfig): FilterTabSection[] {
  return [
    {
      label: "Paid media and advertising",
      items: [
        {
          iconChip: { variant: "blue", icon: PersonWithCheckIcon },
          title: "Lookalike audiences",
          description: `Improve ROAS by building an audience of high-value customer profiles in ${warehouse.name} and uploading it to your ad platforms.`,
        },
        {
          iconChip: { variant: "green", icon: TargetIcon },
          title: "Retargeting",
          description:
            "Build an audience of customer profiles to retarget high-propensity users and drive conversions.",
        },
        {
          iconChip: { variant: "yellow", icon: EyeSlashIcon },
          title: "Suppression",
          description: `Improve ROAS by maintaining a global opt-out list of all your customers in ${warehouse.name} and suppressing existing customers on ad platforms.`,
        },
        {
          iconChip: { variant: "violet", icon: TrendIcon },
          title: "Conversion APIs",
          description: `Send conversion events from ${warehouse.name} to your ad platforms to train ad algorithms and optimize targeting with data on successful conversions.`,
        },
        {
          iconChip: { variant: "violet", icon: GlobeIcon },
          title: "Commerce media networks",
          description: `Build your media network on ${warehouse.name} to create, activate, and monetize premium audiences across 50+ onsite & offsite channels for advertisers.`,
        },
        {
          iconChip: { variant: "yellow", icon: LeversIcon },
          title: "Attribution & measurement",
          description: `Close the loop with attribution and incrementality testing in ${warehouse.name} to improve campaign performance and financial outcomes.`,
        },
        {
          iconChip: { variant: "green", icon: CodeIcon },
          title: "Data clean rooms",
          description: `Push data from ${warehouse.name} to data clean rooms, syncing overlapping audiences to ${integrationsCount}+ destinations for cross-brand and partner collaboration on 1st party data.`,
        },
      ],
    },
    {
      label: "Lifecycle marketing",
      items: [
        {
          iconChip: { variant: "violet", icon: MapIcon },
          title: "Omni-channel journeys",
          description: `Use all of your customer data in ${warehouse.name} to build personalized and dynamic journeys for each individual user to create a cohesive, personalized customer experience across every touchpoint.`,
        },
        {
          iconChip: { variant: "yellow", icon: MegaphoneIcon },
          title: "Winback campaigns",
          description: `Build custom audiences of dormant customers in ${warehouse.name} to re-engage with targeted offers.`,
        },
        {
          iconChip: { variant: "blue", icon: SendIcon },
          title: "Cross-sell & upsell campaigns",
          description:
            "Build audiences of existing, high-propensity customers to send to your marketing tools and retarget to maximize revenue and grow LTV.",
        },
        {
          iconChip: { variant: "violet", icon: SparklesIcon },
          title: "Predictive campaigns",
          description:
            "Use machine learning to predict behaviors and trigger proactive marketing interventions to drive business-specific outcomes (e.g., increasing LTV, reducing churn, etc.).",
        },
        {
          iconChip: { variant: "green", icon: PersonWithCheckIcon },
          title: "Loyalty programs",
          description:
            "Build audiences using core metrics like spending habits, loyalty points, and predictive modeling to deliver personalized experiences to all loyalty members, maximizing LTV and incremental revenue.",
        },
        {
          iconChip: { variant: "blue", icon: MailIcon },
          title: "Dynamic content personalization",
          description:
            "Personalize app and web experiences based on individual user behavior to increase loyalty and conversions.",
        },
      ],
    },
    {
      label: "Customer 360 and operations",
      items: [
        {
          iconChip: { variant: "green", icon: MessageIcon },
          title: "Sales",
          description: `Enrich your CRM & sales tools with product usage data, behavioral data, & health scores from ${warehouse.name} to give your sales reps better visibility into critical account and lead data so they take action in real time.`,
        },
        {
          iconChip: { variant: "blue", icon: SettingsIcon },
          title: "Support",
          description: `Enrich your support tools with product usage data, behavioral data, & ticketing data from ${warehouse.name} to help your support teams prioritize high-value customers and equip them with more context so they can resolve tickets faster.`,
        },
        {
          iconChip: { variant: "violet", icon: MoneyIcon },
          title: "Finance",
          description: `Enrich your finance tools with core metrics like ARR, LTV, and additional product usage data from ${warehouse.name} to provide your finance teams with more accurate and reconciled financial data so they can make better decisions.`,
        },
        {
          iconChip: { variant: "yellow", icon: CodeIcon },
          title: "Product teams",
          description: `Enrich your analytics tools with product usage data from ${warehouse.name} to enable your product teams to better understand what features and products are being used so they can optimize the customer experience.`,
        },
        {
          iconChip: { variant: "violet", icon: ClipboardIcon },
          title: "Operations",
          description: `Enrich your CRM, comms, and project management tools with product usage data, behavioral data, & health scores from ${warehouse.name} to give business teams a complete customer view in the tools they use daily.`,
        },
      ],
    },
  ];
}

function TabSection({ children }: PropsWithChildren) {
  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} columnGap={{ base: "biggest", md: "big" }} rowGap="biggest">
      {children}
    </SimpleGrid>
  );
}

export function WarehouseUseCaseTabs() {
  const { warehouse } = useWarehouseCdp();

  const tabs = useMemo(() => {
    const sections = getTabSections(warehouse);

    return sections.map((section) => ({
      label: section.label,
      items: (
        <TabSection>
          {section.items.map((item) => (
            <TextColumn key={item.title} {...item} />
          ))}
        </TabSection>
      ),
    }));
  }, [warehouse]);

  return (
    <Section my={0} mb={sectionSpacingY}>
      <TextTitle size="lg" align="center" title={`${warehouse.name} CDP use cases`} />
      <FilterTabs mt="bigger" tabs={tabs} />
    </Section>
  );
}
